<script setup lang="ts">
/**
 * Сайдбар трекера — макет «Тихий инструмент» (81:320): 240px, отступ 12,
 * пункт 32px с шагом 34, подпункт 30px с шагом 32, отступы иконок 8/26/50.
 *
 * Проектов может не быть (первый вход, нет членства) — тогда остаётся верхняя
 * навигация и заголовок «Проекты» с плюсом: пустой сайдбар без единого якоря
 * читался бы как поломка.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'
import {
  Book,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Folder,
  GitBranch,
  Kanban,
  List,
  MoreHorizontal,
  PanelLeft,
  Plus,
  Settings,
} from 'lucide-vue-next'
import { AppAvatar, useTheme } from '@vinprop/design-system'

import mark from '@/assets/vinprop-mark.svg'
import logoDark from '@/assets/vinprop-tracker-logo-white.svg'
import logoLight from '@/assets/vinprop-tracker-logo.svg'
import { useNavigationStore } from '@/stores/navigation.store'

const props = defineProps<{
  /** Кто вошёл; до авторизации (DEV-1452) блока пользователя просто нет */
  user?: { name: string; role?: string } | null
  /** Свёрнутый вид: остаются иконки и знак вместо логотипа */
  collapsed?: boolean
}>()

const emit = defineEmits<{ 'toggle-collapse': []; 'add-project': []; 'user-menu': [] }>()

const { t } = useI18n()
const route = useRoute()
const navigation = useNavigationStore()
const { isDark } = useTheme()

const logo = computed(() => {
  if (props.collapsed) return mark

  return isDark.value ? logoDark : logoLight
})

const primaryNav = [
  { key: 'my', to: '/my', icon: CheckSquare, label: 'nav.myTasks' },
  { key: 'projects', to: '/projects', icon: Folder, label: 'nav.projects' },
  { key: 'calendar', to: '/calendar', icon: Calendar, label: 'nav.calendar' },
  { key: 'devops', to: '/devops', icon: GitBranch, label: 'nav.devops' },
  { key: 'knowledge', to: '/kb', icon: Book, label: 'nav.knowledge' },
]

const projectNav = [
  { key: 'board', icon: Kanban, label: 'nav.board' },
  { key: 'issues', icon: List, label: 'nav.issues' },
  { key: 'epics', icon: Folder, label: 'nav.epics' },
  { key: 'settings', icon: Settings, label: 'nav.projectSettings' },
]

/** Счётчик пункта: пока цифры нет — места под неё не занимаем. */
const counterFor = (key: string) => navigation.counters[key as 'my' | 'projects']

/** Точка проекта: свой цвет, если бэкенд его дал, иначе акцент темы. */
const dotColor = (color?: string) => color || 'var(--color-primary-light)'

const isProjectOpen = (key: string) => navigation.expandedKey === key

// Открытый в сайдбаре проект и проект текущего экрана — разные вещи: список
// можно листать, не уходя с доски. Подсветку даёт маршрут, раскрытие — стор.
const activeProjectKey = computed(() => (route.params.key as string | undefined) ?? null)
</script>

<template>
  <aside class="sidebar" :class="{ 'sidebar--collapsed': collapsed }">
    <div class="sidebar__brand">
      <img
        class="sidebar__logo"
        :src="logo"
        alt="VINPROP Tracker"
        :width="collapsed ? 24 : 96"
        height="24"
      />
      <button
        type="button"
        class="sidebar__icon-button"
        :aria-label="collapsed ? t('nav.expand') : t('nav.collapse')"
        @click="emit('toggle-collapse')"
      >
        <PanelLeft :size="16" />
      </button>
    </div>

    <nav class="sidebar__nav">
      <RouterLink
        v-for="item in primaryNav"
        :key="item.key"
        class="nav-item"
        :to="item.to"
        :title="collapsed ? t(item.label) : undefined"
      >
        <component :is="item.icon" class="nav-item__icon" :size="16" />
        <span class="nav-item__label">{{ t(item.label) }}</span>
        <span
          v-if="!collapsed && counterFor(item.key) !== undefined"
          class="nav-item__count"
        >
          {{ counterFor(item.key) }}
        </span>
      </RouterLink>
    </nav>

    <div v-if="!collapsed" class="sidebar__overline">
      <span class="sidebar__overline-text">{{ t('nav.projectsOverline') }}</span>
      <button
        type="button"
        class="sidebar__icon-button"
        :aria-label="t('nav.addProject')"
        @click="emit('add-project')"
      >
        <Plus :size="14" />
      </button>
    </div>

    <div v-if="!collapsed" class="sidebar__projects">
      <template v-for="project in navigation.projects" :key="project.key">
        <button
          type="button"
          class="project"
          :class="{ 'project--active': project.key === activeProjectKey }"
          :aria-expanded="isProjectOpen(project.key)"
          @click="navigation.toggleExpanded(project.key)"
        >
          <span class="project__dot" :style="{ backgroundColor: dotColor(project.color) }" />
          <span class="project__label">{{ project.name }} · {{ project.key }}</span>
          <span v-if="project.openCount !== undefined && !isProjectOpen(project.key)"
            class="project__count">
            {{ project.openCount }}
          </span>
          <ChevronDown v-else-if="isProjectOpen(project.key)" class="project__chevron" :size="14" />
          <ChevronRight v-else class="project__chevron" :size="14" />
        </button>

        <RouterLink
          v-for="sub in isProjectOpen(project.key) ? projectNav : []"
          :key="`${project.key}-${sub.key}`"
          class="sub-item"
          :to="`/p/${project.key}/${sub.key}`"
        >
          <component :is="sub.icon" class="sub-item__icon" :size="14" />
          <span>{{ t(sub.label) }}</span>
        </RouterLink>
      </template>
    </div>

    <div class="sidebar__spacer" />

    <template v-if="user">
      <div class="sidebar__divider" />
      <button
        type="button"
        class="sidebar__user"
        :aria-label="t('nav.userMenu')"
        @click="emit('user-menu')"
      >
        <AppAvatar :name="user.name" :size="24" />
        <span v-if="!collapsed" class="sidebar__user-text">
          <span class="sidebar__user-name">{{ user.name }}</span>
          <span v-if="user.role" class="sidebar__user-role">{{ user.role }}</span>
        </span>
        <MoreHorizontal v-if="!collapsed" :size="14" />
      </button>
    </template>
  </aside>
</template>

<style scoped>
.sidebar {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 240px;
  height: 100vh;
  padding: 16px 12px 12px;
  background-color: var(--color-bg-app);
}

/* Свёрнутый вид макетом не описан: сжимаем до колонки иконок, чтобы кнопка
   панели в шапке не оказалась мёртвой, а доска получила лишние 184px. */
.sidebar--collapsed {
  width: 56px;
  align-items: center;
}

.sidebar--collapsed .sidebar__brand {
  padding: 0;
  flex-direction: column;
  gap: 12px;
  height: auto;
}

.sidebar--collapsed .nav-item,
.sidebar--collapsed .sidebar__user {
  justify-content: center;
  padding: 0;
}

.sidebar--collapsed .nav-item__label {
  display: none;
}

.sidebar__brand {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 8px;
  margin-bottom: 16px;
}

.sidebar__logo {
  width: 96px;
  height: 24px;
  margin-right: auto;
}

.sidebar__icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.sidebar__icon-button:hover {
  color: var(--color-text-primary);
}

.sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 32px;
  padding: 0 8px;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: var(--font-size-body);
  text-decoration: none;
}

.nav-item:hover {
  background-color: var(--color-bg-muted);
}

.nav-item.router-link-active {
  background-color: var(--color-primary-tint);
  color: var(--color-primary-hover);
  font-weight: 500;
}

.nav-item__icon {
  flex-shrink: 0;
}

.nav-item__label {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.nav-item__count {
  margin-left: auto;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

/* 20px, а не 16: в макете между блоками стоит отступ 16, и с обеих сторон
   к нему добавляется тот же зазор 2px, что и между пунктами списка. */
.sidebar__overline {
  display: flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  margin-top: 20px;
}

.sidebar__overline-text {
  margin-right: auto;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.sidebar__projects {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
}

.project {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 32px;
  padding: 0 8px;
  border: none;
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  text-align: left;
  cursor: pointer;
}

.project:hover {
  background-color: var(--color-bg-muted);
}

.project--active {
  font-weight: 500;
}

.project__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
}

.project__label {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.project__count,
.project__chevron {
  margin-left: auto;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.sub-item {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 30px;
  padding: 0 8px 0 26px;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.sub-item:hover {
  background-color: var(--color-bg-muted);
}

.sub-item.router-link-active {
  background-color: var(--color-primary-tint);
  color: var(--color-primary-hover);
  font-weight: 500;
}

.sub-item__icon {
  flex-shrink: 0;
}

.sidebar__spacer {
  flex: 1;
  min-height: 16px;
}

.sidebar__divider {
  height: 1px;
  background-color: var(--color-border);
}

.sidebar__user {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 40px;
  margin-top: 8px;
  padding: 0 8px;
  border: none;
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-muted);
  text-align: left;
  cursor: pointer;
}

.sidebar__user:hover {
  background-color: var(--color-bg-muted);
}

.sidebar__user-text {
  display: flex;
  flex-direction: column;
  margin-right: auto;
  overflow: hidden;
}

.sidebar__user-name {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: 500;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.sidebar__user-role {
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
}
</style>
